import { cloneDeep } from "lodash";
import moment from "moment";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { reportRsFilterOptions } from "../report-rs";
import { ReportRsFilterSettingsForm } from "../settings-form/ReportRsFilterSettingsForm";
import { ReportRsFilterSettings } from "../settings/ReportRsFilterSettings";
import { ReportRsTableDataRequest, CertificateTypeRequest } from "../data-request/ReportRsTableDataRequest";
import { ReportRSRow } from "../table-data/ReportRsRow";
import { ReportRsJournalRequest } from "../journal/ReportRsJournalRequest";
import { CertificateType } from "../../../common/types/roots/fields";
import { Option } from "../../../common/types/Option";
import i18nService from '@/services/i18n';

export class ReportRsFacade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(reportRsFilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        // 1. Валидаций
        if (settings.reportName !== 'RS') {
            throw new Error('error_type_filter_settings')
        }
        if (options.key !== 'RS') {
            throw new Error('error_type_filter_options')
        }

        let reportName = options.reportNames.find(it => it.value === settings.reportName)
        if (!reportName) {
            throw new Error('error_not_found_report_name')
        }
        reportName = { ...reportName }

        let reportType = options.reportTypes.find(it => it.value === settings.reportType)!
        if (!reportType) {
            throw new Error('error_not_found_report_type')
        }
        reportType = { ...reportType }
        
        let classificationType = options.classificationTypes.find(it => it.value === settings.classificationType)
        if (!classificationType) {
            throw new Error('error_not_found_class_type')
        }
        classificationType = { ...classificationType }

        let dataSource = options.dataSources.find(it => it.value === settings.dataSource)
        if (!dataSource) {
            throw new Error('error_not_found_data_source')
        }
        dataSource = { ...dataSource }

        let periodicity = options.periodicities.find(it => it.value === settings.periodicity)
        if (!periodicity) {
            throw new Error('error_not_found_periodicity')
        }
        periodicity = { ...periodicity }

        let measureUnitOption = options.measureUnits.find(it => it.value === settings.measureUnit)
        if (!measureUnitOption) {
            throw new Error('error_not_found_measure_unit')
        }
        measureUnitOption = { ...measureUnitOption }

        let regionOption = options.regions.find(it => it.value === settings.region)
        if (!regionOption) {
            throw new Error('error_not_found_region_option')
        }
        regionOption = { ...regionOption }

        let isRoundUp: boolean = false
        if (settings.roundUpTo != null) {
            isRoundUp = true
        }

        let certificateLevelOption = options.certificateLevels.find(it => it.value === settings.certificateLevel)
        if (!certificateLevelOption) {
            throw new Error('error_not_found_cert_level')
        }
        certificateLevelOption = { ...certificateLevelOption }

        const paymentTypeOption = cloneDeep(
            options.paymentTypes.find(it => it.value === settings.paymentType) ?? null
        )

        const certificateTypeOptions = cloneDeep(
            options.certificateTypes.filter(it => settings.certificateTypes.includes(it.value))
        )

        const statusOptions = cloneDeep(
            options.statuses.filter(it => settings.statuses.includes(it.value))
        )

        // 2. Формирование
        const form: ReportRsFilterSettingsForm = {
            // Base:
            key: 'RS',
            reportName: reportName,
            reportType: reportType,
            classificationType: classificationType,
            dataSource: dataSource,
            periodicity: periodicity,
            // Custom:
            periodFrom: new Date(settings.period.from),
            periodTo: new Date(settings.period.to),
            measureUnit: measureUnitOption,
            region: regionOption,
            certificateLevel: certificateLevelOption,
            paymentType: paymentTypeOption,
            certificateTypes: certificateTypeOptions,
            statuses: statusOptions,
            roundUp: isRoundUp,
            roundUpTo: settings.roundUpTo,
            incomes: cloneDeep(settings.incomes),
            expenses: cloneDeep(settings.expenses),
            isTree: settings.isTree ?? true,
            gus: (settings.gus ?? []).map(code => ({ value: code, label: code })),
        }

        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== 'RS') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (!form.periodFrom || !form.periodTo) {
            throw new Error('error_input_period')
        }

        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }

        if (!form.region) {
            throw new Error('error_input_region')
        }

        if (!form.certificateLevel) {
            throw new Error('error_input_cert_level')
        }

        if (form.certificateTypes.length <= 0) {
            throw new Error('error_input_certificate_type')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        const strPeriodFrom = moment(form.periodFrom).format('YYYY-MM-DD')
        const strPeriodTo = moment(form.periodTo).format('YYYY-MM-DD')

        const settings: ReportRsFilterSettings = {
            // Base:
            reportName: 'RS',
            reportType: form.reportType.value,
            classificationType: form.classificationType.value,
            dataSource: form.dataSource.value,
            periodicity: form.periodicity.value,
            // Custom:
            period: { from: strPeriodFrom, to: strPeriodTo },
            certificateLevel: form.certificateLevel.value,
            certificateTypes: form.certificateTypes.map(it => it.value),
            paymentType: form.paymentType?.value ?? null,
            statuses: form.statuses.map(it => it.value),
            region: form.region.value,
            incomes: cloneDeep(form.incomes),
            expenses: cloneDeep(form.expenses),
            measureUnit: form.measureUnit.value,
            roundUpTo: roundUpTo,
            isTree: form.isTree,
            gus: form.gus.map(it => it.value),
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(reportRsFilterOptions)

        const todayDate = moment().toDate()
        const firstJan = moment().startOf('year').toDate()

        const classificationType = options.classificationTypes.find(it => it.value === 'EXPENSES')!

        const certificateTypes: Option<CertificateType>[] = options.certificateTypes.filter(it =>
            ['INTERNAL', 'MOVEMENT', 'ADJUSTMENT', 'CLARIFICATION'].includes(it.value)
        )

        const emptyForm: ReportRsFilterSettingsForm = {
            key: 'RS',
            // Base fields
            reportName: options.reportNames.find(it => it.value === 'RS')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: classificationType,
            dataSource: options.dataSources.find(it => it.value === 'LOADER')!,
            periodicity: options.periodicities.find(it => it.value === 'PERIOD')!,
            // Custom fields
            periodFrom: firstJan,
            periodTo: todayDate,
            region: null,
            certificateLevel: options.certificateLevels.find(it => it.value === 'ABP')!,
            paymentType: options.paymentTypes.find(it => it.value === 'PAYMENTS')!,
            certificateTypes: certificateTypes,
            statuses: options.statuses.filter(it => it.value === '39')!,
            incomes: cloneDeep(options.incomes),
            expenses: cloneDeep(options.expenses),
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND')!,
            roundUp: false,
            roundUpTo: null,
            isTree: true,
            gus: [],
        }

        return emptyForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        // 1. Валидаций и Формирование данных для таблицы
        if (form.key !== 'RS') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (!form.periodFrom || !form.periodTo) {
            throw new Error('error_input_period')
        }
        if (form.periodFrom > form.periodTo) {
            throw new Error('error_period_range_invalid')
        }
        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }
        if (!form.region) {
            throw new Error('error_input_region')
        }
        if (!form.certificateLevel) {
            throw new Error('error_input_cert_level')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        const strPeriodFrom = moment(form.periodFrom).format('YYYY-MM-DD')
        const strPeriodTo = moment(form.periodTo).format('YYYY-MM-DD')

        const classficationType = form.classificationType.value

        let activeIncomes = form.incomes
            .map(it => ({ key: it.key, selected: it.selected, active: it.active }))
        let activeExpenses = form.expenses
            .map(it => ({ key: it.key, selected: it.selected, active: it.active }))

        let classificators: string[] = []
        if (classficationType === 'INCOMES') {
            activeExpenses = []
            classificators = activeIncomes.map(it => it.key)
        } else if (classficationType === 'EXPENSES') {
            activeIncomes = []
            classificators = activeExpenses.map(it => it.key)
        } else if (classficationType === 'MIXED') {
            classificators = activeIncomes.map(it => it.key)
        }

        let pfType: 1 | 2 | null = null
        if (classficationType === 'MIXED') {
            pfType = 2 // Фиксировано: Платежи
        } else {
            switch (form.paymentType?.value) {
                case 'OBLIGATIONS': pfType = 1; break;
                case 'PAYMENTS': pfType = 2; break;
                default: break;
            }
        }

        const certificateLevelForRequest = classficationType === 'MIXED' ? 'UO' : form.certificateLevel.value

        const certificateTypeIncomeMap: Record<string, CertificateTypeRequest> = {
            'ADJUSTMENT': 'budget_income',
            'MOVEMENT': 'budget_income_movement',
            'CLARIFICATION': 'budget_income_clarification',
        }
        const certificateTypeExpenseMap: Record<string, CertificateTypeRequest> = {
            'INTERNAL': 'individual',
            'MOVEMENT': 'movement',
            'CLARIFICATION': 'budget_clarification',
            'ADJUSTMENT': 'budget_adjustment',
        }

        const certificateTypes: CertificateTypeRequest[] = []
        form.certificateTypes.forEach(it => {
            if (classficationType === 'INCOMES') {
                if (certificateTypeIncomeMap[it.value]) certificateTypes.push(certificateTypeIncomeMap[it.value])
            } else if (classficationType === 'EXPENSES') {
                if (certificateTypeExpenseMap[it.value]) certificateTypes.push(certificateTypeExpenseMap[it.value])
            } else if (classficationType === 'MIXED') {
                // Включаем оба варианта (доходы и расходы) для каждого выбранного типа
                if (certificateTypeIncomeMap[it.value]) certificateTypes.push(certificateTypeIncomeMap[it.value])
                if (certificateTypeExpenseMap[it.value]) certificateTypes.push(certificateTypeExpenseMap[it.value])
            }
        })
        if (certificateTypes.length <= 0) {
            throw new Error('error_input_certificate_type')
        }

        const statuses = form.statuses.map(it => Number(it.value))
        if (statuses.length <= 0) {
            throw new Error('error_input_statuses')
        }

        const params: ReportRsTableDataRequest = {
            bdate: strPeriodFrom,
            edate: strPeriodTo,
            region: form.region.value,
            certificate_level: certificateLevelForRequest as any,
            classification_type: classficationType as any,
            pf_type: pfType,
            certificate_types: certificateTypes,
            statuses: statuses,
            incomes: activeIncomes,
            expenses: activeExpenses,
            measure_unit: form.measureUnit.value,
            round_up_to: roundUpTo,
            locale: i18nService.locale,
            user_id: userId,
            gus: form.gus.map(it => it.value),
        }

        const response = await fetch('/api-py/monitoring/reports-constructor/rs',
            {
                method: 'POST',
                body: JSON.stringify(params)
            }
        )
        if (!response.ok) {
            throw new Error('error_api_report_data_failed')
        }

        const responseTableData: { rows: ReportRSRow[] } = await response.json()
        const tableData: ReportRSRow[] = responseTableData.rows

        // 2. Формирование параметров для выгрузки журнал
        const settings: FilterSettings = this.formToSettings(cloneDeep(form))
        const expenseClassificators = classficationType === 'MIXED'
            ? activeExpenses.map(it => it.key)
            : []
        const guColumnHeaders: Record<string, string> = {
            ru: 'Наименование ГУ',
            kk: 'ММ атауы',
            en: 'GU name',
        }
        const journalParams: ReportRsJournalRequest = {
            reportName: "RS",
            // journal data
            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            // for excel
            classification_type: classficationType as any,
            classificators: classificators,
            expense_classificators: expenseClassificators,
            is_tree: form.isTree,
            rows: tableData,
            certificate_level: certificateLevelForRequest,
            gu_column_header: guColumnHeaders[i18nService.locale] ?? 'Наименование ГУ',

            // Прокинуть параметры запроса для таблицы для пакетной выгрузки
            params: params
        }

        const result: TableDataAndJournalParams = {
            tableData: cloneDeep(tableData),
            journalParams: journalParams
        }

        return result
    }
}
